import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import axios from 'axios'
import { Logo } from '../../components/brand/Logo'

interface PublicPost {
  slug: string
  title: string
  excerpt?: string | null
  tags: string[]
  publishedAt: string
  body?: string
  author: { firstName: string; lastName: string }
}

function PublicShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <header className="border-b border-steel-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/" aria-label="Cima Sport"><Logo className="h-9" /></Link>
          <nav className="flex gap-4 text-sm font-medium">
            <Link to="/blog" className="text-brand-700 hover:underline">Blog</Link>
            {/* Páginas SEO server-rendered: enlace normal, no ruta de la SPA */}
            <a href="/examenes" className="text-brand-700 hover:underline">Exámenes</a>
            <Link to="/" className="text-steel-600 hover:underline">← Volver al sitio</Link>
          </nav>
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-10">{children}</main>
      <footer className="border-t border-steel-200 bg-white py-4 text-center text-xs text-gray-400">
        © {new Date().getFullYear()} Cima Sport — Salud deportiva y ocupacional
      </footer>
    </div>
  )
}

export function BlogListPage() {
  const posts = useQuery({
    queryKey: ['public-blog'],
    queryFn: async () => (await axios.get<PublicPost[]>('/api/public/blog')).data,
  })

  return (
    <PublicShell>
      <h1 className="font-display text-3xl font-bold text-ink">Blog de salud deportiva</h1>
      <p className="mt-1 text-gray-600">Prevención, rehabilitación y salud ocupacional, por nuestro equipo clínico.</p>
      <div className="mt-8 space-y-5">
        {(posts.data ?? []).map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} className="block rounded-2xl border border-steel-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand-500">
            <p className="text-xs text-gray-400">
              {new Date(p.publishedAt).toLocaleDateString('es-CL')} · {p.author.firstName} {p.author.lastName}
            </p>
            <h2 className="mt-1 font-display text-xl font-semibold text-ink">{p.title}</h2>
            {p.excerpt && <p className="mt-1 text-sm text-gray-600">{p.excerpt}</p>}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {p.tags.map((t) => (
                <span key={t} className="rounded-full bg-soft px-2 py-0.5 text-xs text-brand-700">#{t}</span>
              ))}
            </div>
          </Link>
        ))}
        {posts.data?.length === 0 && <p className="text-gray-500">Pronto publicaremos nuestros primeros artículos.</p>}
      </div>
    </PublicShell>
  )
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const post = useQuery({
    queryKey: ['public-blog', slug],
    queryFn: async () => (await axios.get<PublicPost>(`/api/public/blog/${slug}`)).data,
  })

  if (post.isLoading) return <PublicShell><p className="text-gray-500">Cargando…</p></PublicShell>
  if (!post.data) return <PublicShell><p className="text-gray-500">Artículo no encontrado.</p></PublicShell>
  const p = post.data

  return (
    <PublicShell>
      <article>
        <p className="text-xs text-gray-400">
          {new Date(p.publishedAt).toLocaleDateString('es-CL')} · {p.author.firstName} {p.author.lastName}
        </p>
        <h1 className="mt-1 font-display text-3xl font-bold text-ink">{p.title}</h1>
        <div className="prose mt-6 space-y-4 text-[15px] leading-relaxed text-gray-700">
          {(p.body ?? '').split(/\n\n+/).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
        <div className="mt-8 rounded-2xl border border-brand-100 bg-soft p-6 text-center">
          <p className="font-display text-lg font-semibold text-ink">¿Tienes una lesión o necesitas una evaluación?</p>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <Link to="/#contacto" className="inline-block rounded-full bg-brand-600 px-5 py-2.5 font-display text-sm font-semibold text-white hover:bg-brand-700">
              Reserva tu hora
            </Link>
            <a href="/examenes" className="inline-block rounded-full border border-brand-300 bg-white px-5 py-2.5 font-display text-sm font-semibold text-brand-700 hover:border-brand-500 hover:bg-soft">
              Exámenes preocupacionales
            </a>
          </div>
        </div>
      </article>
    </PublicShell>
  )
}
